import {
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  Switch,
} from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import AddBankAccount from 'components/Modal/AddBankAcc'
import SAlert from 'components/SAlert'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, InputGroup } from 'react-bootstrap'
import CurrencyInput from 'react-currency-input-field'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { REVREX_APP_BASE_URL, URLS } from 'resources/constants'
import { useGlClassificationApi } from 'resources/hooks/api/glClassificationApi'
import { useSettingsApi } from 'resources/hooks/api/useSettingsApiHook'
import ClassificationEmptyState from 'scenes/GlClassification/ClassificationEmptyState'
import {
  getAccountSettings,
  getThirdPartyStepsCompleted,
} from 'store/settings/selectors'
import { getBranch } from 'store/user/selectors'
import './BankReconcile.css'
import { classificationBlockingPeriod } from './eligibility'
import ReconcileAction from './ReconcileAction'

interface BankPeriodRow {
  bank: {
    id: number
    account: number
    name?: string | null
    revrex_bank_name?: string | null
    revrex_bank_number?: string | null
  }
  period: {
    month: number
    year: number
  }
  opening_balance: number
  ending_balance: number
  reconciliation_exists: boolean
  has_unreconciled_transactions: boolean
  classification_pending?: boolean
  previous_month_classification_pending?: boolean
  previous_period?: {
    month: number
    year: number
  }
  is_draft?: boolean
  status?: string
}

const SHOW_RECONCILED_KEY = 'bankReconcile.showReconciled'
const SELECTED_ACCOUNT_KEY = 'bankReconcile.selectedAccount'
const SELECTED_YEAR_KEY = 'bankReconcile.selectedYear'

export const formatBankLabel = (
  bank?: {
    name?: string | null
    revrex_bank_name?: string | null
    revrex_bank_number?: string | null
    account?: number | string | null
  },
  numberFirst = false
): string => {
  if (!bank) return 'N/A'
  const name = (bank.revrex_bank_name || '').trim()
  const num = (bank.revrex_bank_number || '').trim()
  if (name && num) return numberFirst ? `${num} - ${name}` : `${name} - ${num}`
  if (name) return name
  if (num) return num
  return (bank.name || '').trim() || (bank.account ? String(bank.account) : '') || 'N/A'
}

const BankReconcile = () => {
  const { t } = useTranslation('scenes.bankReconcile')
  const navigate = useNavigate()
  const branchData = useSelector(getBranch)
  const settingsData = useSelector(getAccountSettings)
  const thirdPartyStepsCompleted = useSelector(getThirdPartyStepsCompleted)
  const { getBankReconcileList } = useGlClassificationApi()
  const { fetchAccountSettingsData } = useSettingsApi()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showQBFlowCompletionModal, setShowQBFlowCompletionModal] =
    useState<boolean>(false)
  const [isQBOFlowCompleted, setIsQBOFlowCompleted] = useState<number>(0)
  const [apiError, setApiError] = useState<string | null>(null)
  const [balances, setBalances] = useState<Record<string, any>>({})
  const [reconcileList, setReconcileList] = useState<BankPeriodRow[]>([])

  const [showReconciled, setShowReconciled] = useState<boolean>(
    () => sessionStorage.getItem(SHOW_RECONCILED_KEY) === 'true'
  )
  const [selectedAccount, setSelectedAccount] = useState<string>(
    () => sessionStorage.getItem(SELECTED_ACCOUNT_KEY) ?? 'All'
  )
  const [selectedYear, setSelectedYear] = useState<string>(
    () => sessionStorage.getItem(SELECTED_YEAR_KEY) ?? 'All'
  )
  const [accountOptions, setAccountOptions] = useState<BankPeriodRow['bank'][]>([])
  const [yearOptions, setYearOptions] = useState<number[]>([])

  useEffect(() => {
    if (settingsData.bank_account_count == 0 && thirdPartyStepsCompleted >= 3) {
      setIsQBOFlowCompleted(1)
    } else if (
      settingsData.bank_account_count > 0 &&
      thirdPartyStepsCompleted >= 3
    ) {
      if (isQBOFlowCompleted == 1) {
        setShowQBFlowCompletionModal(true)
      }
      setIsQBOFlowCompleted(2)
    }
  }, [settingsData.bank_account_count, thirdPartyStepsCompleted])

  useEffect(() => {
    fetchReconcileList()
  }, [selectedAccount, selectedYear, showReconciled])

  const fetchReconcileList = () => {
    const params: any = {
      branch_id: branchData['id'],
    }
    if (selectedYear !== 'All') {
      params.year = selectedYear
    }
    if (selectedAccount !== 'All') {
      params.account_number = selectedAccount
    }
    if (!showReconciled) {
      params.only_unreconciled = true
    }
    setIsLoading(true)
    getBankReconcileList(
      params,
      (message: string, resp: any) => {
        setIsLoading(false)
        // Oldest period first, so the next month to reconcile is at the top.
        const data = [...resp.data.data].sort(
          (a: BankPeriodRow, b: BankPeriodRow) =>
            a.period.year - b.period.year ||
            a.period.month - b.period.month ||
            String(a.bank?.account ?? '').localeCompare(String(b.bank?.account ?? ''))
        )
        setReconcileList(data)
        setAccountOptions((currentOptions) => {
          const uniqueBanks = new Map(
            currentOptions.map((bank) => [bank.account, bank])
          )
          data.forEach((row: BankPeriodRow) => {
            if (row.bank && !uniqueBanks.has(row.bank.account)) {
              uniqueBanks.set(row.bank.account, row.bank)
            }
          })
          return Array.from(uniqueBanks.values())
        })
        setYearOptions((currentOptions) => {
          const years = new Set(currentOptions)
          data.forEach((row: BankPeriodRow) => {
            if (row.period?.year) years.add(row.period.year)
          })
          return Array.from(years).sort((a, b) => b - a)
        })
        const init: any = {}
        data.forEach((row: any) => {
          const key = `${row.bank?.id}-${row.period?.month}-${row.period?.year}`
          init[key] = {
            opening:
              row.opening_balance === 0 ? '' : String(row.opening_balance),
            ending: row.ending_balance === 0 ? '' : String(row.ending_balance),
          }
        })
        setBalances(init)
      },
      (message: string, resp: any) => {
        setIsLoading(false)
        if (resp?.status_code === 400) {
          setApiError(message)
        } else {
          toast.error(message)
        }
      }
    )
  }

  const handleBalanceChange = (
    key: string,
    field: 'opening' | 'ending',
    value: string
  ) => {
    setBalances((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }))
  }
  const handleReconcile = (row: BankPeriodRow) => {
    const key = `${row.bank.id}-${row.period.month}-${row.period.year}`
    const { opening, ending } = balances[key] ?? {}
    const lastDayOfMonth = new Date(row.period.year, row.period.month, 0)
    const endDate = moment(lastDayOfMonth).format('YYYY-MM-DD')
    // All reconcile inputs - including which bank account this is - ride in
    // router navigation state, never the URL.
    navigate(URLS.BANK_RECONCILE_TRANSACTIONS, {
      state: {
        glAccount: String(row.bank.account),
        beginningBalance: opening ?? '',
        endingBalance: ending ?? '',
        endDate,
      },
    })
  }

  const _handleCloseBankAccountModal = () => {
    setShowModal(false)
  }

  const _handleBankAddedSuccess = () => {
    setShowModal(false)
    getSettingsData()
    fetchReconcileList()
  }
  const getSettingsData = () => {
    fetchAccountSettingsData(
      { branch_id: +branchData['id'] },
      (msg: string, resp: any) => {},
      (msg: string, resp: any) => {
        setShowModal(false)
      }
    )
  }

  const isRowCompleted = (row: BankPeriodRow): boolean =>
    row.reconciliation_exists && !row.has_unreconciled_transactions && !row.is_draft

  const isRowDraft = (row: BankPeriodRow): boolean =>
    Boolean(row.is_draft || row.status === 'draft')

  const isPreviousMonthClassificationPending = (row: BankPeriodRow): boolean =>
    !!row.previous_month_classification_pending

  // Get the earliest month per bank id
  const earliestMonthPerBank = useMemo(() => {
    const map: Record<number, { month: number; year: number }> = {}

    reconcileList.forEach((row) => {
      if (isRowCompleted(row)) return
      const bankId = row.bank?.id
      if (!bankId || !row.period) return
      if (!map[bankId]) {
        map[bankId] = { month: row.period.month, year: row.period.year }
      } else {
        const current = map[bankId]
        if (
          row.period.year < current.year ||
          (row.period.year === current.year && row.period.month < current.month)
        ) {
          map[bankId] = { month: row.period.month, year: row.period.year }
        }
      }
    })

    return map
  }, [reconcileList])

  const isEarliestPeriod = (row: BankPeriodRow): boolean => {
    const earliest = earliestMonthPerBank[row.bank?.id]
    if (!earliest) return false
    return (
      earliest.month === row.period?.month && earliest.year === row.period?.year
    )
  }

  const getActionTooltip = (row: BankPeriodRow): string => {
    const reasons: string[] = []
    if (!isEarliestPeriod(row)) {
      reasons.push(t('tooltip.disable'))
    }
    if (isPreviousMonthClassificationPending(row)) {
      reasons.push(
        t(
          'tooltip.previous_month_classification_pending',
          'Previous month classification is pending. Complete classification before reconciling this month.'
        )
      )
    }

    if (reasons.length === 0) {
      return t('tooltip.enable')
    }

    return reasons.join(' • ')
  }

  const MONTH_NAMES = [
    '',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  function monthTransform(month: any) {
    const monthName: any = {
      '1': t('month.01', { ns: 'resources.form-validator' }),
      '2': t('month.02', { ns: 'resources.form-validator' }),
      '3': t('month.03', { ns: 'resources.form-validator' }),
      '4': t('month.04', { ns: 'resources.form-validator' }),
      '5': t('month.05', { ns: 'resources.form-validator' }),
      '6': t('month.06', { ns: 'resources.form-validator' }),
      '7': t('month.07', { ns: 'resources.form-validator' }),
      '8': t('month.08', { ns: 'resources.form-validator' }),
      '9': t('month.09', { ns: 'resources.form-validator' }),
      '10': t('month.10', { ns: 'resources.form-validator' }),
      '11': t('month.11', { ns: 'resources.form-validator' }),
      '12': t('month.12', { ns: 'resources.form-validator' }),
    }
    return monthName[month] || MONTH_NAMES[month]
  }

  // Order banks appear in so consecutive rows for the same bank share a row shade
  const bankRowShade = useMemo(() => {
    const shades: Record<number, string> = {}
    accountOptions.forEach((bank, index) => {
      shades[bank.account] = index % 2 === 0 ? 'bank-row-even' : 'bank-row-odd'
    })
    return shades
  }, [accountOptions])

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid">
        <div
          className="d-flex justify-content-between align-items-center flex-wrap pb-3 pt-3"
          id="ux_page_title"
        >
          <div>
            <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">
              {t('pageTitle')}
            </h1>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <FormControl
              size="small"
              className="bank-reconcile-select"
            >
              <InputLabel id="reconcile-bank-label">
                {t('label.bank', 'Bank')}
              </InputLabel>
              <Select
                labelId="reconcile-bank-label"
                value={selectedAccount}
                label={t('label.bank', 'Bank')}
                MenuProps={{ className: 'bank-reconcile-menu' }}
                onChange={(event) => {
                  const value = event.target.value
                  sessionStorage.setItem(SELECTED_ACCOUNT_KEY, value)
                  setSelectedAccount(value)
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {accountOptions.map((bank) => (
                  <MenuItem key={bank.account} value={String(bank.account)}>
                    {formatBankLabel(bank)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              className="bank-reconcile-select"
            >
              <InputLabel id="reconcile-year-label">
                {t('table.year', 'Year')}
              </InputLabel>
              <Select
                labelId="reconcile-year-label"
                value={selectedYear}
                label={t('table.year', 'Year')}
                MenuProps={{ className: 'bank-reconcile-menu' }}
                onChange={(event) => {
                  const value = event.target.value
                  sessionStorage.setItem(SELECTED_YEAR_KEY, value)
                  setSelectedYear(value)
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={String(year)}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl component="fieldset" variant="standard">
              <FormControlLabel
                className="bank-reconcile-switch-label"
                control={
                  <Switch
                    checked={showReconciled}
                    onChange={(event) => {
                      const checked = event.target.checked
                      sessionStorage.setItem(
                        SHOW_RECONCILED_KEY,
                        String(checked)
                      )
                      setShowReconciled(checked)
                    }}
                    size="small"
                  />
                }
                label={t('label.show_reconciled', 'Show reconciled')}
                labelPlacement="top"
              />
            </FormControl>
          </div>
        </div>
        <div className="filter-btn">
          <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
            <div className="loader"></div>
          </div>
        </div>
        <div className="executive-dashboard p-2 ">
          <div className="table-responsive list-table table-scroll bank-reconcile-table-wrap">
            <table className="table table-default">
              <thead>
                <tr className="align-middle">
                  <th className="text-center w-5">{t('table.bank')}</th>
                  <th className="text-center w-5">{t('table.month')}</th>
                  <th className="text-center w-5">{t('table.year')}</th>
                  <th className="text-center w-5">{t('filter.status')}</th>
                  <th className="text-center w-5">
                    {t('table.beginning_balance')}
                  </th>
                  <th className="text-center w-5">
                    {t('table.closing_balance')}
                  </th>
                  <th className="text-center w-5">{t('table.action')}</th>
                </tr>
              </thead>
              <tbody>
                {reconcileList.length > 0 ? (
                  reconcileList.map((row) => {
                    const key = `${row.bank.id}-${row.period.month}-${row.period.year}`
                    const vals = balances[key] ?? { opening: '', ending: '' }
                    const bankLabel = formatBankLabel(row.bank, true)
                    const title = `${bankLabel} - ${monthTransform(row.period.month)} - ${row.period.year}`
                    const earliestPeriod = isEarliestPeriod(row)
                    const classificationPeriod = classificationBlockingPeriod(row)
                    const classificationBlocked = classificationPeriod !== null
                    const reconcileDisabled = !earliestPeriod || classificationBlocked
                    const classificationTooltip = classificationPeriod
                      ? row.classification_pending
                        ? t('tooltip.current_classification_pending', {
                          month: monthTransform(classificationPeriod.month),
                          year: classificationPeriod.year,
                        })
                        : t('tooltip.previous_classification_pending')
                      : ''
                    return (
                      <tr
                        key={key}
                        className={`align-middle ${bankRowShade[row.bank.account]}`}
                      >
                        <td className="text-center bank-reconcile-bank-cell" title={title}>
                          {bankLabel}
                        </td>
                        <td className="text-center">
                          {monthTransform(row.period.month)}
                        </td>
                        <td className="text-center">{row.period.year}</td>
                        <td className="text-center">
                          <h6>
                            {isRowCompleted(row) ? (
                              <Badge bg="success">
                                {t('status.completed')}
                              </Badge>
                            ) : isRowDraft(row) ? (
                              <Badge bg="info">
                                {t('status.draft', 'Draft')}
                              </Badge>
                            ) : (
                              <Badge bg="warning">{t('status.pending')}</Badge>
                            )}
                          </h6>
                        </td>
                        <td className="bank-reconcile-balance-cell">
                          <InputGroup size="sm">
                            <InputGroup.Text>$</InputGroup.Text>
                            <CurrencyInput
                              className="form-control"
                              placeholder="0.00"
                              value={vals.opening}
                              onValueChange={(value: string | undefined) =>
                                handleBalanceChange(key, 'opening', value ?? '')
                              }
                              allowDecimals
                              decimalsLimit={2}
                              decimalSeparator="."
                              groupSeparator=","
                              disableAbbreviations
                              allowNegativeValue={false}
                              // Beginning balance carries over from the previous period's closing balance.
                              disabled
                            />
                          </InputGroup>
                        </td>
                        <td className="bank-reconcile-balance-cell">
                          <InputGroup size="sm">
                            <InputGroup.Text>$</InputGroup.Text>
                            <CurrencyInput
                              className="form-control"
                              placeholder="0.00"
                              value={vals.ending}
                              onValueChange={(value: string | undefined) =>
                                handleBalanceChange(key, 'ending', value ?? '')
                              }
                              allowDecimals
                              decimalsLimit={2}
                              decimalSeparator="."
                              groupSeparator=","
                              disableAbbreviations
                              allowNegativeValue={false}
                              disabled={isRowCompleted(row) || !earliestPeriod}
                            />
                          </InputGroup>
                        </td>
                        <td className="text-center">
                          {isRowCompleted(row) ? (
                            <Link
                              component="button"
                              variant="body2"
                              title={t('button.viewDetails')}
                              underline={'none'}
                              onClick={() => handleReconcile(row)}
                              className="bank-reconcile-action-link"
                            >
                              {t('button.viewDetails')}
                            </Link>
                          ) : (
                            <ReconcileAction
                              label={isRowDraft(row) ? t('button.resume', 'Resume') : t('button.action')}
                              actionTooltip={isRowDraft(row) ? t('tooltip.resume', 'Continue reconciling remaining transactions for this period') : undefined}
                              previousMonthTooltip={t('tooltip.disable')}
                              classificationTooltip={classificationTooltip}
                              disabled={reconcileDisabled}
                              classificationBlocked={classificationBlocked}
                              previousMonthBlocked={!earliestPeriod}
                              onClick={() => handleReconcile(row)}
                            />
                          )}
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className="d-flex justify-content-center align-items-center py-4">
                        {apiError ? (
                          <Alert
                            variant="danger"
                            className="d-flex align-items-center justify-content-center"
                          >
                            <i
                              className="fa fa-exclamation-triangle fa-lg "
                              aria-hidden="true"
                            />
                            <span className="ms-2">{apiError}</span>
                          </Alert>
                        ) : (
                          <ClassificationEmptyState
                            title={t('empty_state.title')}
                            description={t('empty_state.description')}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {(showModal && (
        <AddBankAccount
          isShow={showModal}
          onHide={_handleCloseBankAccountModal}
          onSuccess={_handleBankAddedSuccess}
          branch_id={branchData.id}
        />
      )) || <></>}

      <SAlert
        HtmlMsg={t('alert.msg', { url: REVREX_APP_BASE_URL })}
        show={showQBFlowCompletionModal}
        icon="success"
        title={t('alert.title')}
        confirmBtnText={t('alert.close.title')}
        onConfirm={() => {
          setShowQBFlowCompletionModal(false)
        }}
        showCancel={false}
        onCancel={() => setShowQBFlowCompletionModal(false)}
      />
    </div>
  )
}
export default BankReconcile
